import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productService } from '../services/api';
import { wishlistService } from '../services/wishlist';
import Breadcrumbs from '../components/Breadcrumbs';
import Spinner from '../components/Spinner';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('android');
  const [loading, setLoading] = useState(true);
  const [isInCart, setIsInCart] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [productId]);

  useEffect(() => {
    if (product) {
      checkCart();
      setIsInWishlist(wishlistService.isInWishlist(product.productId));
    }
  }, [product, selectedPlatform]);

  const checkCart = () => {
    if (!product) return;
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const cartKey = `${product.productId}-${selectedPlatform}`;
    setIsInCart(cart.some(item => item.cartKey === cartKey));
  };

  const loadProduct = async () => {
    try {
      const response = await productService.getById(productId);
      setProduct(response.data.product);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const [addingToCart, setAddingToCart] = useState(false);

  const addToCart = () => {
    setAddingToCart(true);
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const cartKey = `${product.productId}-${selectedPlatform}`;
    
    cart.push({ 
      ...product, 
      quantity: 1,
      selectedPlatform,
      selectedPrice: pricing.price,
      selectedOriginalPrice: pricing.originalPrice,
      cartKey
    });
    
    localStorage.setItem('cart', JSON.stringify(cart));
    setIsInCart(true);
    setAddingToCart(false);
    
    // Trigger cart badge animation
    const badge = document.querySelector('.cart-badge');
    if (badge) {
      badge.classList.add('cart-add-animation');
      setTimeout(() => badge.classList.remove('cart-add-animation'), 600);
    }
    
    window.dispatchEvent(new Event('storage'));
  };

  const goToCart = () => {
    navigate('/cart');
  };

  const toggleWishlist = () => {
    if (isInWishlist) {
      wishlistService.removeFromWishlist(product.productId);
      setIsInWishlist(false);
    } else {
      wishlistService.addToWishlist({
        id: product.productId,
        name: product.name,
        price: pricing.price,
        image_url: product.imageUrl,
        slug: product.slug || product.productId
      });
      setIsInWishlist(true);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `Check out ${product?.name} on IWantCheats!`;

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    reddit: `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`,
    discord: shareUrl,
    instagram: shareUrl
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopySuccess(true);
    setTimeout(() => {
      setCopySuccess(false);
      setShowShareMenu(false);
    }, 3000);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showShareMenu && !e.target.closest('[data-share-menu]')) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showShareMenu]);

  if (loading) return <div className="loading">Loading...</div>;
  if (!product) return <div className="error">Product not found</div>;

  // Get pricing based on selected platform
  const pricing = product.platforms 
    ? product.platforms[selectedPlatform] 
    : { price: product.price, originalPrice: product.originalPrice };
  
  const hasDiscount = pricing.originalPrice && pricing.originalPrice > pricing.price;
  const discountPercent = hasDiscount 
    ? Math.round(((pricing.originalPrice - pricing.price) / pricing.originalPrice) * 100)
    : 0;
  
  const hasPlatforms = !!product.platforms;

  return (
    <div className="product-detail-page">
      <Breadcrumbs items={[
        { label: 'Home', link: '/' },
        { label: 'Products', link: '/' },
        { label: product.name }
      ]} />
      
      <div className="product-detail-container">
        <div className="product-image-section">
          <img 
            src={product.imageUrl} 
            alt={product.name}
            onError={(e) => e.target.src = '/assets/images/coming-soon.jpg'}
          />
        </div>

        <div className="product-info-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
            <h1 style={{ margin: 0 }}>{product.name}</h1>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={toggleWishlist} style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <img 
                  src={isInWishlist ? '/assets/icons/wishlist.svg' : '/assets/icons/wishlist-outline.svg'} 
                  alt="Wishlist" 
                  style={{ 
                    width: '28px', 
                    height: '28px', 
                    filter: isInWishlist ? 'brightness(0) saturate(100%) invert(27%) sepia(98%) saturate(6943%) hue-rotate(335deg) brightness(98%) contrast(103%)' : 'brightness(0) invert(1)'
                  }}
                />
              </button>
              <div style={{ position: 'relative' }} data-share-menu>
                <button onClick={() => setShowShareMenu(!showShareMenu)} style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.15)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                </button>
                {showShareMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '45px',
                    right: 0,
                    background: 'rgba(15, 20, 45, 0.98)',
                    border: '2px solid rgba(138, 43, 226, 0.5)',
                    borderRadius: '10px',
                    padding: '15px',
                    minWidth: '200px',
                    zIndex: 100,
                    boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                  }}>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                      <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" style={{
                        padding: '12px',
                        background: 'rgba(37, 211, 102, 0.2)',
                        border: '1px solid #25D366',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                        <img src="/assets/icons/whatsapp.svg" alt="WhatsApp" style={{ width: '24px', height: '24px' }} />
                      </a>
                      <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer" style={{
                        padding: '12px',
                        background: 'rgba(0, 136, 204, 0.2)',
                        border: '1px solid #0088cc',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                        <img src="/assets/icons/telegram.svg" alt="Telegram" style={{ width: '24px', height: '24px' }} />
                      </a>
                      <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" style={{
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.2)',
                        border: '1px solid #000',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#fff"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      </a>
                      <a href={shareLinks.reddit} target="_blank" rel="noopener noreferrer" style={{
                        padding: '12px',
                        background: 'rgba(255, 69, 0, 0.2)',
                        border: '1px solid #FF4500',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                        <img src="/assets/icons/reddit.svg" alt="Reddit" style={{ width: '24px', height: '24px' }} />
                      </a>
                    </div>
                    <button onClick={copyLink} style={{
                      marginTop: '12px',
                      width: '100%',
                      padding: '12px',
                      background: copySuccess ? 'rgba(40, 167, 69, 0.2)' : 'rgba(138, 43, 226, 0.2)',
                      border: copySuccess ? '1px solid #28a745' : '1px solid #8a2be2',
                      borderRadius: '8px',
                      color: copySuccess ? '#28a745' : '#8a2be2',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}>{copySuccess ? '✓ Copied!' : '🔗 Copy Link'}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {product.category && (
            <span className="category-badge">{product.category}</span>
          )}

          {hasPlatforms && (
            <div className="platform-selector">
              <h3>Select Platform:</h3>
              <div className="platform-options">
                <label className={selectedPlatform === 'android' ? 'active' : ''}>
                  <input
                    type="radio"
                    name="platform"
                    value="android"
                    checked={selectedPlatform === 'android'}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                  />
                  <span className="platform-label">
                    <strong>Android</strong>
                    <span className="platform-price">${product.platforms.android.price}</span>
                  </span>
                </label>
                <label className={selectedPlatform === 'ios' ? 'active' : ''}>
                  <input
                    type="radio"
                    name="platform"
                    value="ios"
                    checked={selectedPlatform === 'ios'}
                    onChange={(e) => setSelectedPlatform(e.target.value)}
                  />
                  <span className="platform-label">
                    <strong>iOS</strong>
                    <span className="platform-price">${product.platforms.ios.price}</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="price-container">
            {hasDiscount && (
              <>
                <span className="original-price">${pricing.originalPrice}</span>
                <span className="discount-badge">-{discountPercent}%</span>
              </>
            )}
            <span className="current-price">${pricing.price}</span>
          </div>

          <div className="stock-info">
            {product.stock > 0 ? (
              <span className="in-stock">
                 <span style={{ color: '#fff' }}>Stock:</span>
                <span style={{ color: '#4CAF50' }}> AVAILABLE ✓</span> 
              </span>
            ) : (
              <span className="out-of-stock">
                 <span style={{ color: '#fff' }}>Stock:</span>
                <span style={{ color: '#f44336' }}> OUT OF STOCK ✗</span>
              </span>
            )}
          </div>

          <div className="description-section">
            <h3>Description</h3>
            <p>{product.description}</p>
          </div>

          {product.features && (
            <div className="features-section">
              <h3>Features</h3>
              <ul>
                {product.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="purchase-section">
            {isInCart ? (
              <button 
                onClick={goToCart}
                className="add-to-cart-btn-large go-to-bag"
              >
                Go to Bag
              </button>
            ) : (
              <button 
                onClick={addToCart}
                disabled={product.stock === 0 || addingToCart}
                className="add-to-cart-btn-large"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
              >
                {addingToCart ? (
                  <><Spinner size="20px" /> Adding...</>
                ) : (
                  `Add to Cart - $${pricing.price.toFixed(2)}`
                )}
              </button>
            )}
          </div>

          {product.deliveryInfo && (
            <div className="delivery-info">
              <h4>Delivery Information</h4>
              <p>{product.deliveryInfo}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
